import { PsyToolsId } from '../../components/meet/tools-panel/types';
import { Breakpoint } from '../../utils/breakpoint';

import bg1 from '../../assets/images/virtual-bg/bg1.png';
import bg2 from '../../assets/images/virtual-bg/bg2.png';
import bg3 from '../../assets/images/virtual-bg/bg3.png';
import bg4 from '../../assets/images/virtual-bg/bg4.png';
import bg5 from '../../assets/images/virtual-bg/bg5.png';
import bg6 from '../../assets/images/virtual-bg/bg6.png';
import cab1 from '../../assets/images/virtual-bg/cab1.png';
import cab2 from '../../assets/images/virtual-bg/cab2.png';
import livingroom1 from '../../assets/images/virtual-bg/livingroom1.png';
import livingroom2 from '../../assets/images/virtual-bg/livingroom2.png';
import livingroom3 from '../../assets/images/virtual-bg/livingroom3.png';
import livingroom4 from '../../assets/images/virtual-bg/livingroom4.png';
import livingroom5 from '../../assets/images/virtual-bg/livingroom5.png';

import type { GroupConnectionRequestPayload } from '../group-connection-request/types';

import type {
  GroupConnectionRequestViewModel,
  MeetBackgroundOption,
  MeetingParticipantViewModel,
  RemoteVideoLike,
} from './types';

export const meetBackgrounds: MeetBackgroundOption[] = [
  { id: 'bg1', src: bg1 },
  { id: 'bg2', src: bg2 },
  { id: 'bg3', src: bg3 },
  { id: 'bg4', src: bg4 },
  { id: 'bg5', src: bg5 },
  { id: 'bg6', src: bg6 },
  { id: 'cab1', src: cab1 },
  { id: 'cab2', src: cab2 },
  { id: 'livingroom1', src: livingroom1 },
  { id: 'livingroom2', src: livingroom2 },
  { id: 'livingroom3', src: livingroom3 },
  { id: 'livingroom4', src: livingroom4 },
  { id: 'livingroom5', src: livingroom5 },
];

export const getConsultationClientName = (consultation?: {
  client_full_name?: string;
  client?: { full_name?: string };
}) => consultation?.client_full_name || consultation?.client?.full_name;

export const getGroupDisplayName = (clientFullName?: string, consultationClientName?: string) =>
  clientFullName?.split('-')[0]?.trim() ||
  clientFullName ||
  consultationClientName?.split('-')[0]?.trim() ||
  consultationClientName;

export const isLiveMediaStream = (stream?: MediaStream | null) => {
  if (!stream) {
    return false;
  }

  const tracks = stream.getTracks();

  return tracks.length > 0 && tracks.some((track) => track.readyState === 'live');
};

export const getScreenShareFeed = <T extends RemoteVideoLike>(videos: T[]) =>
  videos.find((video) => video.isScreenShare && isLiveMediaStream(video.stream));

export const getRegularVideos = <T extends RemoteVideoLike>(videos: T[]) =>
  videos.filter((video) => !video.isScreenShare);

export const getExpectedParticipantIds = (videos: RemoteVideoLike[]) =>
  videos.map((video) => String(video.identity ?? video.display ?? '')).filter(Boolean);

export const getParticipantDisplayNames = (videos: RemoteVideoLike[]) => {
  const participantDisplayNames: Record<string, string> = {};

  videos.forEach((video, index) => {
    const id = String(video.identity ?? video.display ?? '');

    if (id) {
      participantDisplayNames[id] = String(video.display ?? video.identity ?? `Участник ${index + 1}`);
    }
  });

  return participantDisplayNames;
};

const SCREEN_SHARE_DISPLAY_SUFFIX = ' (Screen Share)';

export const normalizeParticipantDisplayName = (name?: string | null): string | undefined => {
  let normalized = String(name ?? '').trim();

  if (!normalized || normalized.toLowerCase() === 'undefined' || normalized.toLowerCase().startsWith('undefined ')) {
    return undefined;
  }

  normalized = normalized.replace(/\s-\s(client|psychologist)$/i, '').trim();

  if (!normalized || normalized.toLowerCase() === 'undefined') {
    return undefined;
  }

  return normalized;
};

const getScreenShareOwnerIdentity = (screenShareFeed?: RemoteVideoLike) => {
  const identity = String(screenShareFeed?.identity ?? '').trim();

  if (identity) {
    return identity;
  }

  const display = String(screenShareFeed?.display ?? '').trim();

  if (display.endsWith(SCREEN_SHARE_DISPLAY_SUFFIX)) {
    return display.slice(0, -SCREEN_SHARE_DISPLAY_SUFFIX.length);
  }

  return display;
};

export const getScreenShareOwnerDisplayName = (
  screenShareFeed?: RemoteVideoLike,
  regularVideos: RemoteVideoLike[] = [],
) => {
  const ownerIdentity = normalizeParticipantDisplayName(getScreenShareOwnerIdentity(screenShareFeed));

  if (!ownerIdentity) {
    return undefined;
  }

  const ownerVideo = regularVideos.find(
    (video) => normalizeParticipantDisplayName(String(video.identity ?? '').trim()) === ownerIdentity,
  );
  const ownerDisplayName = normalizeParticipantDisplayName(ownerVideo?.display);

  return ownerDisplayName || ownerIdentity;
};

export const getVisibleConnectionRequests = (
  awaitingList: GroupConnectionRequestPayload[],
  dismissedIds: Set<string>,
  allowedPendingIds: Set<string> = new Set(),
): GroupConnectionRequestViewModel[] =>
  awaitingList
    .filter((participant) => !dismissedIds.has(participant.identity) && !allowedPendingIds.has(participant.identity))
    .map((participant) => ({
      id: participant.identity,
      displayName: participant.displayName ?? participant.identity,
      isAllowedPending: false,
    }));

export const getVisibleAwaitingParticipants = (
  awaitingList: GroupConnectionRequestPayload[],
  hiddenIds: Set<string>,
  allowedPendingIds: Set<string> = new Set(),
): GroupConnectionRequestViewModel[] =>
  awaitingList
    .filter((participant) => !hiddenIds.has(participant.identity))
    .map((participant) => ({
      id: participant.identity,
      displayName: participant.displayName ?? participant.identity,
      isAllowedPending: allowedPendingIds.has(participant.identity),
    }));

export const getMeetingParticipants = (
  remoteVideosForMeeting: RemoteVideoLike[],
  psychologistId?: number | null,
): MeetingParticipantViewModel[] =>
  getRegularVideos(remoteVideosForMeeting).map((video) => {
    const id = String(video.identity ?? video.display ?? '');
    const displayName = String(video.display ?? '');
    const isPsychologist =
      (psychologistId != null && id === String(psychologistId)) || displayName.endsWith(' - psychologist');

    return { id, displayName, isPsychologist };
  });

interface VideoPanelDisplayModeArgs {
  activeTool: PsyToolsId | 0;
  breakpoint: Breakpoint;
  formFactor: string;
  isPsychologist: boolean;
  hasColorCodeActiveTab: boolean;
  cloudsSelectedImage: number | null;
  cloudsHideVideoForClient: boolean;
  remoteVideosForMeeting: RemoteVideoLike[];
  localVideo: unknown;
  screenShareFeed?: RemoteVideoLike;
}

export const getVideoPanelDisplayMode = ({
  activeTool,
  breakpoint,
  formFactor,
  isPsychologist,
  hasColorCodeActiveTab,
  cloudsSelectedImage,
  cloudsHideVideoForClient,
  remoteVideosForMeeting,
  localVideo,
  screenShareFeed,
}: VideoPanelDisplayModeArgs) => {
  if (activeTool === PsyToolsId.CLOUDS && !isPsychologist && cloudsHideVideoForClient) {
    return null;
  }

  if (screenShareFeed && activeTool > 0) {
    // Групповая терапия (МАК/эмоции): камеры сверху, демонстрация — оверлей на инструменте
    if (activeTool === PsyToolsId.MAC_CARDS || activeTool === PsyToolsId.EMOTIONS) {
      return null;
    }

    return 'right-column';
  }

  if ((activeTool === PsyToolsId.WHEEL || activeTool === PsyToolsId.SAND_THERAPY) && formFactor === 'mobile-portrait' && !isPsychologist) {
    return 'side-by-side';
  }

  if (activeTool === PsyToolsId.CLOUDS && formFactor === 'mobile-portrait' && !isPsychologist) {
    return 'side-by-side';
  }

  if ((activeTool === PsyToolsId.WHEEL || activeTool === PsyToolsId.SAND_THERAPY) && breakpoint >= Breakpoint.Lg && isPsychologist) {
    return 'right-column';
  }

  if ((activeTool === PsyToolsId.WHEEL || activeTool === PsyToolsId.SAND_THERAPY) && !isPsychologist) {
    return 'right-column';
  }

  if (activeTool === PsyToolsId.COLOR_CODE && hasColorCodeActiveTab && isPsychologist) {
    return 'right-column';
  }

  if (activeTool === PsyToolsId.CLOUDS && cloudsSelectedImage !== null) {
    return 'right-column';
  }

  if (activeTool === PsyToolsId.DPDG || (breakpoint <= Breakpoint.Sm && activeTool > 0)) {
    return 'compact';
  }

  if (remoteVideosForMeeting.length + (localVideo ? 1 : 0) >= 3) {
    return 'grid';
  }

  return 'default';
};

export const getSharingPersonName = (
  isPsychologist: boolean,
  psychologistName?: string,
  clientName?: string,
  screenShareOwnerName?: string,
) => {
  const ownerName = normalizeParticipantDisplayName(screenShareOwnerName);

  if (ownerName) {
    return ownerName;
  }

  if (isPsychologist) {
    return normalizeParticipantDisplayName(clientName) || 'Клиент';
  }

  return normalizeParticipantDisplayName(psychologistName) || 'Психолог';
};
